<template>
  <div class="bxp-form">
    <span class="bxp-desktop-header">
      <h1>
        Start aligning your strategy & culture by filling in the form below.
      </h1>
    </span>
    <span class="bxp-mobile-header">
      <h1>
        <strong>Plans to suit any size team </strong>
      </h1>
      <p class="heading-sub-text">
        Get bountiXP <strong>Professional FREE for 14 days.</strong> You can downgrade or upgrade your trial plan at any time. Find a perfect fit, no credit card required. Have a question? Not sure which plan is right for you, or do you need a <a href="/start-conversation?cta=free_trial_form_sidebar">custom quote</a>?<br />
      </p>
      <p class="heading-sub-text">
        <i>(No credit card required)</i> 
      </p>
    </span>
    <div class="bxp-form-fields">
      <v-text-field
        id="first_name"
        label="First name"
        prepend-inner-icon="fas fa-user"
        color="secondary"
        @change="updateFirstName($event)"
        :append-outer-icon="fields.firstName.validationIcon"
        :success="fields.firstName.valid"
        :error="fields.firstName.valid === false"
        :hint="fields.firstName.validationMessage"
        :persistent-hint="true"
        v-scroll-to="'#first_name'"
      />

      <v-text-field
        label="Last name"
        prepend-inner-icon="fas fa-user"
        color="secondary"
        @change="updateLastName($event)"
        :append-outer-icon="fields.lastName.validationIcon"
        :success="fields.lastName.valid"
        :error="fields.lastName.valid === false"
        :hint="fields.lastName.validationMessage"
        :persistent-hint="true"
      />

      <v-autocomplete
        id="country"
        label="Country"
        v-model="countryValue"
        :items="fields.country.options"
        item-text="name"
        item-value="code"
        prepend-inner-icon="fas fa-map-marker-alt"
        color="secondary"
        :success="fields.country.valid"
        v-scroll-to="{
          el: '#country',
          offset: -40
        }"
      >
        <template v-slot:append-outer>
          <v-tooltip left :open-on-hover="countryValue !== 'ZA'" max-width="320px">
            <template v-slot:activator="{ on, attrs }">
              <v-icon 
                v-bind="attrs" 
                v-on="countryValue !== 'ZA' ? on : null" 
                :color="countryValue !== 'ZA' ? 'accent' : 'primary'"
              >{{ fields.country.validationIcon }}</v-icon>
            </template>
            <span>{{ fields.country.validationMessage }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <vuetify-phone-field
        id="mobile_number"
        label="Mobile number"
        color="secondary"
        @updated="mobileNumberValue = $event.number"
        :region="countryValue"
        @valid="updateMobileNumberValidation($event)"
        :success="fields.mobileNumber.valid"
        :error="fields.mobileNumber.valid === false"
        :hint="fields.mobileNumber.validationMessage"
        prepend-inner-icon="fas fa-phone"
        :append-outer-icon="fields.mobileNumber.validationIcon"
        :persistent-hint="true"
        autocomplete="nope"
        v-scroll-to="'#mobile_number'"
      >
        <template v-slot:append>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="bxp-tooltip-inner">far fa-question-circle</v-icon>
            </template>
            We will not share your mobile number with third party suppliers nor use it to spam you.
          </v-tooltip>
        </template>
      </vuetify-phone-field>

      <v-text-field
        class="bxp-small-icon"
        label="Work email address"
        color="secondary"
        prepend-inner-icon="fas fa-envelope"
        v-model="emailValue"
        :append-outer-icon="fields.email.validationIcon"
        :success="fields.email.valid"
        :error="fields.email.valid === false"
        :hint="fields.email.validationMessage"
        :persistent-hint="true"
        :loading="fields.email.loading"
        autocomplete="nope"
      />

      <v-checkbox
        class="bxp-checkbox"
        v-model="fields.termsAccepted"
        color="secondary"
      >
        <template v-slot:label
          >I agree to bountiXP's
          <a href="/terms-of-use/" target="_blank" @click.stop>terms of use</a>
          and
          <a href="/privacy-policy/" target="_blank" @click.stop>privacy policy</a>
        </template>
      </v-checkbox>
    </div>

    <div class="text-center">
      <bxp-button
        color="primary"
        @click="ajaxSubmitSignUpForm"
        :disabled="!formValid || loading"
        :loading="loading"
      >Submit</bxp-button>
    </div>
    
  </div>
</template>

<script>
import axios from "axios";
import { formBus } from "@/formBus";

export default {
  data() {
    return {
      loading: false
    };
  },
  computed: {
    fields() {
      return formBus.fields
    },
    formValid() {
      return (
        formBus.fields.firstName.valid &&
        formBus.fields.lastName.valid &&
        formBus.fields.country.valid &&
        formBus.fields.mobileNumber.valid &&
        formBus.fields.email.valid &&
        formBus.fields.termsAccepted
      );
    },
    countryValue: {
      get() {
        return formBus.fields.country.value;
      },
      set(val) {
        formBus.fields.country.value = val;
        this.validateCountry(val);
      }
    },
    mobileNumberValue: {
      get() {
        return formBus.fields.mobileNumber.value;
      },
      set(val) {
        formBus.fields.mobileNumber.value = val;
      }
    },
    emailValue: {
      get() {
        return formBus.fields.email.value;
      },
      set(val) {
        formBus.fields.email.loading = true;
        val = val.trim();
        formBus.fields.email.value = val;
        this.resetValidation("email", val);
        if (formBus.fields.email.timer !== null) {
          clearTimeout(formBus.fields.email.timer);
        }
        formBus.fields.email.timer = setTimeout(
          async val => {
            const emailValidation = await this.validateEmail(val);
            this.updateValidation(
              "email",
              emailValidation.valid,
              emailValidation.message
            );
            formBus.fields.email.loading = false;
          },
          2500,
          val
        );
      }
    }
  },
  methods: {
    async ajaxSubmitSignUpForm() {
      this.loading = true;
      //Verify reCAPTCHA
      const recaptchaResponse = await this.executeRecaptcha();
      if (!recaptchaResponse) {
        return false;
      }
      //Build the data for the server
      const formData = new FormData();
      formData.append("action", "bxp_ajax_submit_sign_up_form");
      formData.append("recaptcha_response", recaptchaResponse);
      formData.append("nonce", this.ajaxParams.nonce);
      formData.append("first_name", formBus.fields.firstName.value);
      formData.append("last_name", formBus.fields.lastName.value);
      formData.append("country", formBus.fields.country.value);
      formData.append("mobile_number", formBus.fields.mobileNumber.value);
      formData.append("email", formBus.fields.email.value);
      //Add referrer data to the submission
      formData.append("http_referrer", this.referrerParams.httpReferrer);
      formData.append("cta_referrer", this.referrerParams.ctaReferrer);
      formData.append("product", this.referrerParams.product);
      formData.append("product_title", this.referrerParams.productTitle);
      formData.append("plan", this.referrerParams.plan);
      formData.append("plan_title", this.referrerParams.planTitle);
      formData.append("term", this.referrerParams.term);
      formData.append("currency", this.referrerParams.currency);
      formData.append("user_count", this.referrerParams.userCount);
      formData.append("monthly_base_cost", this.referrerParams.monthlyBaseCost);
      formData.append("addons", this.referrerParams.addons);
      //Add the Hubspot Tracking Code and Client IP to the submission
      formData.append("hubspotutk", this.getHsTrackingCode());

      //Send to the server
      axios.post(this.ajaxParams.ajaxurl, formData).then(response => {
        if (response.data.error) {
          formBus.errorMessage = response.data.error;
          formBus.error = true;
        }
        if (response.data.success) {
          this.triggerGtmThankYouEvent();
          this.$router.push({
            name: "verification",
            query: { email: formBus.fields.email.value }
          });
        }
        this.loading = false;
      });
    },
    updateFirstName(val) {
      formBus.fields.firstName.value = val;
      let valid = null;
      let message = null;
      if (val === "") {
        valid = false;
        message = "This field is incomplete";
      } else {
        valid = true;
        message = null;
      }
      this.updateValidation("firstName", valid, message);
    },
    updateLastName(val) {
      formBus.fields.lastName.value = val;
      let valid = null;
      let message = null;
      if (val === "") {
        valid = false;
        message = "This field is incomplete";
      } else {
        valid = true;
        message = null;
      }
      this.updateValidation("lastName", valid, message);
    },
    async validateEmail(email) {
      //Empty field
      if (email === "") {
        return {
          valid: false,
          message: "This field is incomplete"
        };
      }
      //Email format
      const validCriteria = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!validCriteria.test(email)) {
        return {
          valid: false,
          message: "Please enter a valid email address"
        };
      }
      //Uses a domain that's not blacklisted as non-work emails
      if (!this.isWorkEmail(email)) {
        return {
          valid: false,
          message: "Please enter your work email address"
        };
      }
      return {
        valid: true,
        message: null
      };
    }
  }
};
</script>